import React, { useContext, useState, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RoomContext } from '../context/RoomContext';
import { FaMapMarkerAlt, FaWifi, FaCoffee, FaParking, FaUtensils, FaGlassCheers } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { hotelAssets } from '../assets/hotelAssets';
import { formatDisplayDate, toDateString, calculateNights } from '../utils/dateUtils';
import { HOTEL_ADDRESS, HOTEL_LOCATION_URL, HOTEL_NAME } from '../config/hotel';
import API_BASE_URL from '../api';

const Reviews = lazy(() => import('../pages/Reviews'));

const RoomDetails = () => {
  const { rooms, allRooms, loading, selectedRoomDetails, setSelectedRoomDetails } =
    useContext(RoomContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const roomList = rooms.length > 0 ? rooms : allRooms;
  const room = roomList.find((r) => String(r.id) === String(id));
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  if (!room) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600">{loading ? 'Loading room details...' : 'Room not found.'}</p>
        {!loading && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/rooms')}>
            Back to rooms
          </button>
        )}
      </div>
    );
  }

  const handleNavigation = async () => {
    const checkInStr = toDateString(selectedRoomDetails.checkInDate);
    const checkOutStr = toDateString(selectedRoomDetails.checkOutDate);

    if (!checkInStr || !checkOutStr) {
      alert('Please search for rooms and select check-in / check-out dates first.');
      navigate('/rooms');
      return;
    }

    setCheckingAvailability(true);

    try {
      const totalGuests =
        Number(selectedRoomDetails.adults || 0) + Number(selectedRoomDetails.kids || 0);
      const response = await axios.get(`${API_BASE_URL}/inventory/available`, {
        params: {
          checkIn: checkInStr,
          checkOut: checkOutStr,
          maxPerson: totalGuests || 1,
          _: Date.now(),
        },
        timeout: 15000,
      });
      const isStillAvailable = (response.data || []).some(
        (availableRoom) => String(availableRoom.id) === String(room.id)
      );

      if (!isStillAvailable) {
        alert('This room is no longer available for the selected dates. Please choose another room.');
        navigate('/rooms');
        return;
      }
    } catch (error) {
      console.error('Error checking room availability:', error);
      alert('Could not confirm room availability. Please try again.');
      return;
    } finally {
      setCheckingAvailability(false);
    }

    const numberOfDays =
      calculateNights(checkInStr, checkOutStr) || selectedRoomDetails.numberOfDays || 1;
    const roomPrice = Number(room.price) || 0;
    const totalRoomPrice = roomPrice * numberOfDays;
    const taxes = Math.round(totalRoomPrice * 0.12) || 0;
    const totalAmount = totalRoomPrice + taxes || 0;

    setSelectedRoomDetails((prevDetails) => ({
      ...prevDetails,
      roomId: room.id,
      roomType: room.name,
      checkInDate: checkInStr,
      checkOutDate: checkOutStr,
      numberOfDays,
      price: totalRoomPrice,
      mealPlan: 'CP',
      mealCost: 0,
      taxes,
      totalAmount,
      adults: selectedRoomDetails.adults,
      kids: selectedRoomDetails.kids,
    }));

    navigate('/customerdetails');
};


  return (
    <article>
      <Helmet>
        <title>{room.name} | {HOTEL_NAME}</title>
        <meta name="description" content={`Details and booking for ${room.name}. Enjoy luxury and comfort with our top-notch amenities.`} />
      </Helmet>
      <header
        className="relative flex justify-center items-center bg-cover bg-center min-h-[240px] h-[40vh] sm:min-h-[320px] sm:h-[50vh] lg:min-h-[400px] lg:h-[560px] max-h-[560px]"
        style={{ backgroundImage: `url(${hotelAssets.roomDetailsHero})` }}
      >
        <div className="absolute w-full h-full bg-black/20"></div>
        <h1 className="relative z-10 text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white font-primary text-center px-4">
          {room.name}
        </h1>
      </header>
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 py-8 lg:py-16">
          <div className="w-full lg:w-[58%] order-2 lg:order-1">
            <h2 className="h2  text-3xl md:text-4xl">{room.name}</h2>
            <p className="mb-8 text-lg">{room.description}</p>  
            {/* <img className="mb-8 w-full rounded shadow-lg" src={room.imageLg} alt={room.name} /> */}
            <section>
              <h3 className="h3 text-2xl mt-8">Hotel Rules</h3>
              <ul className="mb-6 list-disc list-inside text-lg">
                <li>Quiet hours are observed from 10 PM to 7 AM</li>
                <li>Visitors must depart by 10 PM.</li>
                <li>Please maintain the room in good condition</li>
                <li>Use the in-room safe for storing valuables</li>
                <li>Contact the front desk in case of emergencies</li>
              </ul>
            </section>
            <section>
              <h3 className="h3 text-2xl mt-8">Room Facilities</h3>
              <ul className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-base sm:text-lg">
                <li className="flex items-center gap-2">
                  <FaWifi className="text-blue-500 shrink-0" /> Wifi
                </li>
                <li className="flex items-center gap-2">
                  <FaCoffee className="text-yellow-500 shrink-0" /> Coffee
                </li>
                <li className="flex items-center gap-2">
                  <FaParking className="text-green-500 shrink-0" /> Parking Space
                </li>
                <li className="flex items-center gap-2">
                  <FaUtensils className="text-orange-500 shrink-0" /> Breakfast
                </li>
                <li className="flex items-center gap-2 sm:col-span-2">
                  <FaGlassCheers className="text-purple-500 shrink-0" /> Drinks
                </li>
              </ul>
            </section>
          </div>
          <aside className="w-full lg:w-[42%] order-1 lg:order-2 lg:sticky lg:top-28 lg:self-start">
            <ReservationSummary
              room={room}
              handleNavigation={handleNavigation}
              checkingAvailability={checkingAvailability}
            />
          </aside>
          
        </div>
        
       </section>
       <section className="w-full bg-gray-50 border-t border-gray-100">
        <Suspense fallback={<p className="text-center text-gray-500 py-12">Loading reviews...</p>}>
          <Reviews embedded />
        </Suspense>
       </section>

    </article>
  );
};

const ReservationSummary = ({ room, handleNavigation, checkingAvailability }) => {
  const { selectedRoomDetails } = useContext(RoomContext);

  const totalRoomPrice = room.price * (selectedRoomDetails.numberOfDays || 1);
  const taxes = Math.round(totalRoomPrice * 0.12);

  return (
    <div className="p-4 border rounded shadow-lg bg-white">
      <h3 className="text-xl font-semibold">{room.name}</h3>
      <div className="my-4">
        <div className="grid grid-cols-2 gap-2">
          <p><strong>Check-In:</strong> {formatDisplayDate(selectedRoomDetails.checkInDate)}</p>
          <p><strong>Check-Out:</strong> {formatDisplayDate(selectedRoomDetails.checkOutDate)}</p>
          <p><strong>Nights:</strong> {selectedRoomDetails.numberOfDays || 1}</p>
          <p><strong>Adults:</strong> {selectedRoomDetails.adults || 0}</p>
          <p><strong>Kids:</strong> {selectedRoomDetails.kids || 0}</p>
        </div>
      </div>

      <p className="mb-4 text-gray-700">complementory breackfast</p>

      <ul className="list-disc pl-5">
        <li className="text-green-600">Free Cancellation till check-in</li>
        <li className="text-blue-500">Couples are allowed at our Property</li>
      </ul>

      <p className="text-2xl font-bold">
        ₹ {totalRoomPrice} + ₹ {taxes} taxes & fees
      </p>

      <button
        className="btn btn-lg btn-primary w-full bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
        onClick={handleNavigation}
        disabled={checkingAvailability}
      >
        {checkingAvailability ? 'CHECKING...' : 'BOOK THIS NOW'}
      </button>
      
      <div className="mt-4 border-t pt-4">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
            <p className="text-lg font-bold text-blue-500">3.9</p>
          </div>
          <div className="ml-4">
            <p className="font-bold text-blue-700">Very Good</p>
          </div>
        </div>
        
        <div className="flex items-center mt-4">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
            <FaMapMarkerAlt className="text-blue-500" />
          </div>
          <div className="ml-4">
            <a
              href={HOTEL_LOCATION_URL}
              className="flex items-center"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View on Google Maps"
            >
              <span className="text-sm sm:text-base break-words">{HOTEL_ADDRESS}</span>
            </a>
            <p className="text-sm text-blue-500 cursor-pointer">
              <a
                href={HOTEL_LOCATION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                See on Map
              </a>
            </p>
          </div>
        </div>
      </div>
     
    </div>
    
  
  );
};

export default RoomDetails;
